'use client';

import { c as _c } from "react/compiler-runtime";
import { MouseEvent } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { trendingCategorySlugsAtom } from '@/atoms/trendingSearchesAtom';
import { Link } from '@/i18n/navigation';
import { getUserImageSrc } from '@/utility/images';
import { getShowroomHrefArgs } from '@/utility/urls';
import { MOBILE_TRENDING_SEARCH_COUNT, TrendingLicense, TrendingSearchesArm, TrendingTerm, getTrendingSearches } from './trendingSearches';
type TrendingSearchesPanelProps = {
  arm: TrendingSearchesArm;
  locale: string;
  onTrendingSearch: (term: TrendingTerm, categorySlugs: string[] | undefined) => void;
  onLicense: (license: TrendingLicense) => void;
};
const TrendingUpIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 20 20" className="size-5 shrink-0" aria-hidden="true"><path d="M4.5 13.5 13.5 4.5M13.5 4.5H7.75M13.5 4.5v5.75" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const TrendingSearchesPanel = t0 => {
  const $ = _c(19);
  const {
    arm,
    locale,
    onTrendingSearch,
    onLicense
  } = t0;
  const t = useTranslations("header");
  const imageBaseUrl = useImageBaseUrl();
  const {
    terms,
    licenses
  } = getTrendingSearches(arm, locale);
  const categorySlugs = useAtomValue(trendingCategorySlugsAtom);
  let t1;
  if ($[0] !== t) {
    t1 = t("trendingSearchesLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const trendingLabel = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("popularLicensesLabel");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const licensesLabel = t2;
  let t3;
  if ($[4] !== onLicense) {
    t3 = (event, license) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      onLicense(license);
    };
    $[4] = onLicense;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const openLicense = t3;
  let t4;
  if ($[6] !== trendingLabel) {
    t4 = <p className="px-4 font-made text-xs leading-[18px] font-medium text-neutral-700">{trendingLabel}</p>;
    $[6] = trendingLabel;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== categorySlugs || $[9] !== onTrendingSearch) {
    t5 = (term, index) => <li key={term.name} className={index >= MOBILE_TRENDING_SEARCH_COUNT ? "hidden min-[834px]:block" : undefined}><button type="button" data-kameleoon-goal="trending-search-term" className="flex h-11 w-full cursor-pointer items-center gap-3 px-6 text-left text-base focus-visible:outline focus-visible:outline-black" onClick={() => onTrendingSearch(term, term.categoryId === null ? undefined : categorySlugs[term.categoryId])}><TrendingUpIcon /><span className="min-w-0 truncate">{term.name}</span></button></li>;
    $[8] = categorySlugs;
    $[9] = onTrendingSearch;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== licensesLabel) {
    t6 = <p className="mt-4 px-4 font-made text-xs leading-[18px] font-medium text-neutral-700">{licensesLabel}</p>;
    $[11] = licensesLabel;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = "flex gap-2 overflow-x-auto px-4 pt-2 pb-4";
  let t8;
  if ($[13] !== imageBaseUrl || $[14] !== openLicense) {
    t8 = license_0 => <li key={license_0.slug} className="shrink-0"><Link href={getShowroomHrefArgs({
        userSlug: license_0.slug
      })} className="flex w-20 cursor-pointer flex-col items-center gap-2 text-center focus-visible:outline focus-visible:outline-black" data-kameleoon-goal="trending-search-license" data-tracking={`Searchbar - Popular license - ${license_0.name}`} onClick={event_1 => openLicense(event_1, license_0)}><img className="size-20 rounded-lg bg-white object-cover shadow-[0_2px_4px_rgba(48,49,51,0.1),0_0_1px_rgba(48,49,51,0.05)]" src={getUserImageSrc(imageBaseUrl, license_0.userId, 160)} alt="" draggable={false} loading="eager" /><span className="line-clamp-2 text-xs leading-4 font-semibold tracking-wide">{license_0.name}</span></Link></li>;
    $[13] = imageBaseUrl;
    $[14] = openLicense;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = licenses.map(t8);
  let t10;
  if ($[16] !== licensesLabel || $[17] !== t9) {
    t10 = <ul aria-label={licensesLabel} className={t7}>{t9}</ul>;
    $[16] = licensesLabel;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return <div onMouseDown={_temp} className="absolute top-full left-0 z-10 max-h-[calc(var(--visual-viewport-height)-var(--header-height)-var(--header-banner-height)-5rem)] w-full overflow-y-auto overscroll-contain border border-neutral-700 bg-white py-4">{t4}<ul aria-label={trendingLabel}>{terms.map(t5)}</ul>{t6}{t10}</div>;
};
function _temp(event_0) {
  return event_0.preventDefault();
}